/**
 * AutoGradeHistoryPanel — Timeline view of auto-grade history.
 * LAUNCH:C6.P3
 */
import React, { useState, useEffect } from 'react';
import { Clock, Check, X, RefreshCw } from 'lucide-react';
import { getAutoGradeHistory } from '../../services/vaultApi';

const STATUS_ICONS = {
  completed: Check,
  failed: X,
  processing: RefreshCw,
  queued: Clock,
  pending: Clock,
};

function formatTimestamp(dateStr) {
  /* c8 ignore next */
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// The served shape for a Decimal field is a string. Coerced in ONE place so every row -- and
// anything added to this panel later -- gets the same answer, rather than a guard repeated per
// render site and forgotten on the next line.
const gradeOf = (v) => {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
};

export default function AutoGradeHistoryPanel({ vaultItemId, history: historyProp }) {
  // V-3 (D60): SELF-FETCH. This used to render whatever `history` prop it was handed, and its
  // only caller passed `item.auto_grade_history` -- a field NOTHING has ever served. So the
  // panel always got [] and printed "No grading history" on top of real events. That is a false
  // statement rather than an empty state, which is worse than the invisible badge in V-1.
  //
  // The prop is still honoured when a caller supplies real rows (tests, and any future parent
  // that already holds them); it is only the absent case that now goes to the API.
  const [fetched, setFetched] = useState(null);

  useEffect(() => {
    if (historyProp && historyProp.length > 0) return;
    if (vaultItemId == null) return;
    let cancelled = false;
    getAutoGradeHistory(vaultItemId)
      .then((data) => {
        if (!cancelled) setFetched(data?.items ?? []);
      })
      .catch(() => {
        // A history timeline is not worth breaking the detail pane over; an empty list here is
        // honest -- we genuinely do not know -- and the panel already renders that state.
        if (!cancelled) setFetched([]);
      });
    return () => {
      cancelled = true;
    };
  }, [vaultItemId, historyProp]);

  const history = historyProp && historyProp.length > 0 ? historyProp : fetched;

  if (!history || history.length === 0) {
    return (
      <div
        className="border-t border-zinc-800 pt-4 mt-4"
        aria-label="Auto-grade history"
      >
        <p className="text-xs text-zinc-500 text-center py-3">No grading history</p>
      </div>
    );
  }

  return (
    <div
      className="border-t border-zinc-800 pt-4 mt-4"
      aria-label="Auto-grade history"
    >
      <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-2">
        Grading History
      </p>
      <div role="list" className="space-y-2 max-h-40 overflow-y-auto">
        {history.map((entry, idx) => {
          /* c8 ignore next */
          const Icon = STATUS_ICONS[entry.status] || Clock;
          const isCompleted = entry.status === 'completed';

          return (
            <div
              key={entry.event_id || idx}
              role="listitem"
              className="flex items-start gap-2 text-xs"
            >
              <Icon className="w-3 h-3 mt-0.5 text-zinc-500 flex-shrink-0" aria-hidden="true" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <span className={isCompleted ? 'text-green-400 font-medium' : 'text-zinc-400'}>
                    {/* ⛔ grade_value IS A STRING ON THE WIRE. It is Decimal on
                        AutoGradeEventSummary, and pydantic v2 serialises Decimal to JSON as
                        "9.4" rather than 9.4 -- so .toFixed threw "is not a function" and took
                        down the ENTIRE history list, not the one row that happened to be
                        completed. The fixtures passed numbers, so the suite could not see it.

                        ⚠ Number(null) is 0 and Number('') is 0, so a `!= null` test alone would
                        print "Grade: 0.0" for a missing value -- a fabricated grade in a panel
                        whose whole job is to show what was actually measured. gradeOf() returns
                        null for anything unusable and the row falls back to its status. */}
                    {isCompleted && gradeOf(entry.grade_value) != null
                      ? `Grade: ${gradeOf(entry.grade_value).toFixed(1)}`
                      : entry.status?.charAt(0).toUpperCase() + entry.status?.slice(1)}
                  </span>
                  {entry.confidence != null && (
                    <span className="text-zinc-500">{entry.confidence}%</span>
                  )}
                </div>
                <p className="text-zinc-600">{formatTimestamp(entry.created_at)}</p>
              {/* c8 ignore next */}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
